<script setup lang="ts">
import { dir } from '@@/bits/current_dir'
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { defineAsyncComponent } from '@@/bits/vue'
import { useExpandedPostStore } from '@@/pinia/expanded_post'
import { useSurfaceStore } from '@@/pinia/surface'
import { useCommentEditMode, useCommentsStore } from '@@/pinia/surface_comments'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { DropZone } from '@@/pinia/surface_drag_and_drop_events_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import type { Comment, CommentId, Id, Post, User } from '@@/types'
import SurfaceCommentAttachmentDropZoneOverlay from '@@/vuecomponents/SurfaceCommentAttachmentDropZoneOverlay.vue'
import SurfacePostComment from '@@/vuecomponents/SurfacePostComment.vue'
import { useDropToCreateComment } from '@@/vuecomposables/useDropToCreateComment'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

const SurfaceAddComment = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceAddComment.vue'))

const props = defineProps<{
  post: Post
  user: User
  commentIds: CommentId[]
  comments: { [id: number]: Comment }
  /**
   * Can add comment if wall setting allows, and user has Commenter right.
   */
  canAddComment: boolean
  displayedPostWidth: number
}>()

defineEmits<{
  (e: 'create', payload: { postId: Id; body?: string; attachment?: string }): void
  (e: 'edit', payload: { id: CommentId; body?: string; attachment?: string }): void
  (e: 'show-comment-action-menu', event: { commentId: CommentId }): void
  (e: 'stop-editing', commentId: CommentId): void
}>()

const { isPhone } = storeToRefs(useSurfaceContainerSizeStore())
const { isStream, isSectionBreakout, colorScheme, isScreenshotMode } = storeToRefs(useSurfaceStore())
const { canIModerate } = storeToRefs(useSurfacePermissionsStore())
const { expandedPostCid, xExpandedPostPanel } = storeToRefs(useExpandedPostStore())

const {
  startWritingNewComment,
  stopWritingNewComment,
  updateNewCommentBody,
  updateInlineCommentInputLastTypedInMs,
  isCommentBeingEdited,
} = useCommentsStore()

const postCid = computed(() => props.post.cid)

const isNewPostExpandedPanelOpen = computed(() => {
  return !!expandedPostCid.value && !device.app
})

const commentsList = computed<Comment[]>(() => {
  const commentsFromIds: Comment[] = props.commentIds.map((id) => props.comments[id])
  if (isAppUsing('commentModeration')) {
    return commentsFromIds.filter((comment) => {
      if (!comment.is_awaiting_approval) {
        return true
      }
      const isAuthor = props.user.id === comment.user_id
      return isAuthor || canIModerate.value
    })
  }
  return commentsFromIds
})

const onAddCommentInput = ({ body }: { body: string }): void => {
  updateNewCommentBody({ postCid: props.post.cid, body })
  updateInlineCommentInputLastTypedInMs()
}

const { shouldEditCommentsInModal } = useCommentEditMode()

const rootEl = ref<HTMLDivElement>()
useDropToCreateComment({
  elementRef: rootEl,
  postCid,
})

const xAddCommentForm = computed<boolean>(() => {
  if (isScreenshotMode.value) return false
  return props.canAddComment
})
</script>

<template>
  <div
    ref="rootEl"
    :data-drop-zone-name="DropZone.POST_COMMENT_SECTION"
    :class="[
      'relative',
      'bg-transparent',
      // If user can comment, give `SurfaceAddComment` control of the bottom padding
      // so its tap area can be bigger.
      !xAddCommentForm && 'pb-3',
    ]"
  >
    <template v-if="commentIds.length > 0">
      <SurfacePostComment
        v-for="comment in commentsList"
        :id="comment.id"
        :key="comment.id"
        :class="[
          'mt-3',
          {
            'px-3': !isStream,
            'px-4': isStream,
          },
        ]"
        :is-being-edited="!isNewPostExpandedPanelOpen && isCommentBeingEdited(comment.id) && !shouldEditCommentsInModal"
        :comment="comment"
        :post-color="post.color"
        :displayed-post-width="displayedPostWidth"
        @show-comment-action-menu="$emit('show-comment-action-menu', { commentId: comment.id })"
        @stop-editing="$emit('stop-editing', comment.id)"
        @edit="$emit('edit', $event)"
      />
    </template>
    <SurfaceAddComment
      v-if="xAddCommentForm"
      :post="post"
      :user="user"
      :can-mention="!isSectionBreakout"
      :color-scheme="colorScheme"
      :dir="dir()"
      :is-stream="isStream"
      :is-phone="isPhone"
      :displayed-post-width="displayedPostWidth"
      @start-writing-comment="startWritingNewComment($event)"
      @stop-writing-comment="stopWritingNewComment($event)"
      @update-comment-body="onAddCommentInput($event)"
      @create-comment="$emit('create', $event)"
    />

    <!-- Media comment drop zone overlay -->
    <SurfaceCommentAttachmentDropZoneOverlay
      v-if="!xExpandedPostPanel"
      :class="[
        'absolute',
        {
          // When there's no comments, the height of this component isn't enough to show the drop message
          // so we 'steal' some space from above (`SurfacePostSocialContentTopBar`).
          '-top-6': commentIds.length === 0,
          'top-0': commentIds.length > 0,
        },
        'inset-x-1',
        'bottom-1',
        'z-10',
      ]"
      :post-cid="post.cid"
      :dark-mode="colorScheme === 'dark'"
      :border-radius="8"
      :text-inline-padding="displayedPostWidth < 360 ? 12 : 24"
    />
  </div>
</template>
